"use client";
import {
  createContext,
  ReactNode,
  Suspense,
  use,
  useCallback,
  useState,
} from "react";
import { trackEvent as dispatchEvent } from "./tracker";
import { TrackingEvent } from "./types";
import { AutoTracker } from "./components/AutoTracker";
import {
  isEcommerceEvent,
  useEcommerceTrackEvent,
} from "./e-commerce/worker/use-ecommerce-worker";
import { getCookie } from "@cappelendamm/cookie";
import { COOKIE_CAMPAIGN_SOURCE } from "@/routing";
import { attachCampaignToEcommerceEvent } from "./e-commerce/attach-campaign";

type TrackingContext = {
  events: TrackingEvent[];
  trackEvent: (event: TrackingEvent) => void;
};

export const TrackingContext = createContext<TrackingContext>({
  events: [],
  trackEvent: dispatchEvent,
});

export function useTrackEvent() {
  const { trackEvent } = use(TrackingContext);
  return trackEvent;
}

export function TrackingContextProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<TrackingEvent[]>([]);
  const trackEventHandler = useCallback((event: TrackingEvent) => {
    const trackedEvent = attachCampaignToEcommerceEvent(
      event,
      getCookie(COOKIE_CAMPAIGN_SOURCE) || undefined,
    );
    dispatchEvent(trackedEvent);
    setEvents((prev) => [...prev, trackedEvent]);
  }, []);

  const trackEcommerceEvent = useEcommerceTrackEvent({
    onEvent: trackEventHandler,
  });

  const trackEvent = useCallback(
    (event: TrackingEvent) => {
      if (isEcommerceEvent(event)) trackEcommerceEvent(event);
      else trackEventHandler(event);
    },
    [trackEcommerceEvent, trackEventHandler],
  );

  return (
    <TrackingContext value={{ trackEvent, events }}>
      {children}
      {/* Suspense boundary required: AutoTracker uses useSearchParams for tracking. */}
      <Suspense>
        <AutoTracker />
      </Suspense>
    </TrackingContext>
  );
}
